import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  PlanType,
  BillingPeriod,
  PlanLimits,
  PLAN_LIMITS,
  isWithinLimit,
  calculateUsagePercentage,
  getLookupKey,
} from "@/lib/stripe";
import { isValidStripeUrl } from "@/lib/security/urlSanitizer";
import { isInFreeTrial as checkFreeTrial, getFreeTrialDaysLeft } from "@/lib/freeTrial";

// Interface pour l'usage
interface UsageData {
  analyses_ia_used: number;
  ocr_used: number;
}

// Interface pour les infos de cycle de facturation
interface BillingCycleInfo {
  billingCycleStart: string | null;
  nextResetDate: string | null;
}

// Interface pour le suivi de facturation (anti-cheat)
interface BillingUsageData {
  chantiersCreatedCount: number;
  freeDeletionUsed: boolean;
  cycleStart: string | null;
  cycleEnd: string | null;
}

// Interface complete pour le context
interface SubscriptionContextType {
  // Etat de base
  subscribed: boolean;
  plan: PlanType;
  planName: string;
  status: string;
  loading: boolean;

  // Acces invite (code promo)
  hasInviteAccess: boolean;

  // Periode et dates
  billingPeriod: BillingPeriod | null;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isTrialing: boolean;
  trialEnd: string | null;

  // Limites et usage
  limits: PlanLimits;
  usage: UsageData;

  // Cycle de facturation
  billingCycleStart: string | null;
  nextResetDate: string | null;

  // Suivi de facturation (anti-cheat)
  billingUsage: BillingUsageData;

  // Blocage (unpaid, canceled avec Stripe, ou past_due)
  isBlocked: boolean;

  // Indique si un abonnement Stripe existe (vs trial DB-only)
  hasStripeSubscription: boolean;

  // Free trial sans CB (basé sur user_metadata)
  isFreeTrial: boolean;
  freeTrialDaysLeft: number;

  // Fonctions de verification des limites
  canCreateChantier: () => { allowed: boolean; currentCount: number; maxAllowed: number; message?: string };
  canAddUser: (currentCount: number) => boolean;
  canAddMembre: (currentCount: number) => boolean;
  canUseAnalyseIA: () => boolean;
  canUseOCR: () => boolean;

  // Fonction pour vérifier si la suppression est gratuite
  checkDeletionAllowed: () => { isFree: boolean; message: string };

  // Rafraîchir les données de billing usage
  refreshBillingUsage: () => Promise<void>;

  // Pourcentages d'usage
  getAnalyseIAUsagePercent: () => number;
  getOCRUsagePercent: () => number;

  // Quotas restants
  getRemainingAnalysesIA: () => number;
  getRemainingOCR: () => number;

  // Actions
  refreshSubscription: () => Promise<void>;
  openCheckout: (plan: Exclude<PlanType, 'free'>, billingPeriod: BillingPeriod) => Promise<void>;
  openCheckoutLegacy: (priceId: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  incrementAnalyseIA: (chantierId?: string) => Promise<void>;
  incrementOCR: (chantierId?: string) => Promise<void>;

  // ID entreprise pour les appels RPC
  entrepriseId: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

// Hook safe pour les composants optionnels (Sentry, analytics)
// Retourne null au lieu de throw si le context n'est pas disponible
export const useSubscriptionSafe = () => {
  return useContext(SubscriptionContext);
};

// Limites par defaut (plan free)
const DEFAULT_LIMITS: PlanLimits = PLAN_LIMITS.free;
const DEFAULT_USAGE: UsageData = { analyses_ia_used: 0, ocr_used: 0 };
const DEFAULT_BILLING_USAGE: BillingUsageData = {
  chantiersCreatedCount: 0,
  freeDeletionUsed: false,
  cycleStart: null,
  cycleEnd: null,
};

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  // Etat de base
  const [subscribed, setSubscribed] = useState(false);
  const [plan, setPlan] = useState<PlanType>("free");
  const [status, setStatus] = useState("none");
  const [loading, setLoading] = useState(true);

  // Acces invite
  const [hasInviteAccess, setHasInviteAccess] = useState(false);

  // Periode et dates
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);

  // Limites et usage
  const [limits, setLimits] = useState<PlanLimits>(DEFAULT_LIMITS);
  const [usage, setUsage] = useState<UsageData>(DEFAULT_USAGE);

  // Cycle de facturation
  const [billingCycleStart, setBillingCycleStart] = useState<string | null>(null);
  const [nextResetDate, setNextResetDate] = useState<string | null>(null);
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);

  // Suivi de facturation (anti-cheat)
  const [billingUsage, setBillingUsage] = useState<BillingUsageData>(DEFAULT_BILLING_USAGE);

  // Compteur d'echecs consecutifs pour fail-closed progressif (ref pour eviter re-renders)
  const consecutiveFailures = useRef(0);

  // Indique si un abonnement Stripe existe (vs trial DB-only)
  const [hasStripeSubscription, setHasStripeSubscription] = useState(false);

  // Free trial sans CB
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [freeTrialDaysLeft, setFreeTrialDaysLeft] = useState(0);

  const { toast } = useToast();

  // Calcul: l'utilisateur est-il bloqué?
  // - past_due / unpaid → toujours bloqué (problème de paiement Stripe)
  // - canceled + Stripe → bloqué (annulation volontaire d'un vrai abonnement)
  // - canceled sans Stripe → PAS bloqué (trial DB-only expiré → redirigé vers payment-setup)
  // - incomplete → PAS bloqué (checkout abandonné → redirigé vers payment-setup)
  const isBlocked = status === 'past_due' || status === 'unpaid' || (status === 'canceled' && hasStripeSubscription);

  // Calculer la prochaine date de reset basée sur billingCycleStart
  const calculateNextResetDate = useCallback((billingStart: string | null): string | null => {
    if (!billingStart) return null;

    const startDate = new Date(billingStart);
    const billingDay = startDate.getDate();
    const now = new Date();
    const currentDay = now.getDate();

    let nextReset: Date;
    if (currentDay >= billingDay) {
      // On est après le jour de facturation ce mois, donc prochain reset le mois prochain
      nextReset = new Date(now.getFullYear(), now.getMonth() + 1, billingDay);
    } else {
      // On est avant le jour de facturation, donc reset ce mois
      nextReset = new Date(now.getFullYear(), now.getMonth(), billingDay);
    }

    return nextReset.toISOString();
  }, []);

  // Nom affichable du plan
  const planName = plan === 'free' ? 'Gratuit' :
                   plan === 'starter' ? 'Starter' :
                   plan === 'pro' ? 'Pro' : 'Entreprise';

  // =========================================================================
  // FONCTIONS DE VERIFICATION DES LIMITES
  // =========================================================================

  // Vérifier si l'utilisateur peut créer un chantier (basé sur billingUsage)
  const canCreateChantier = useCallback(() => {
    const maxAllowed = limits.max_chantiers;
    const currentCount = billingUsage.chantiersCreatedCount;

    // Plan illimité
    if (maxAllowed === -1) {
      return { allowed: true, currentCount, maxAllowed };
    }

    // Vérifier la limite
    if (currentCount >= maxAllowed) {
      const cycleEndDate = billingUsage.cycleEnd
        ? new Date(billingUsage.cycleEnd).toLocaleDateString('fr-FR')
        : 'la fin du cycle';

      return {
        allowed: false,
        currentCount,
        maxAllowed,
        message: `Vous avez atteint la limite de ${maxAllowed} chantiers pour ce mois. Passez au plan supérieur ou attendez le ${cycleEndDate}.`,
      };
    }

    return { allowed: true, currentCount, maxAllowed };
  }, [limits.max_chantiers, billingUsage.chantiersCreatedCount, billingUsage.cycleEnd]);

  // Vérifier si la suppression est gratuite ou compte
  const checkDeletionAllowed = useCallback(() => {
    if (!billingUsage.freeDeletionUsed) {
      return {
        isFree: true,
        message: 'Cette suppression est gratuite et ne comptera pas dans votre quota.',
      };
    }
    return {
      isFree: false,
      message: 'Vous avez déjà utilisé votre suppression gratuite ce mois. Cette suppression ne réduira pas votre compteur de chantiers créés.',
    };
  }, [billingUsage.freeDeletionUsed]);

  const canAddUser = useCallback((currentCount: number) => {
    return isWithinLimit(currentCount, limits.max_users);
  }, [limits.max_users]);

  const canAddMembre = useCallback((currentCount: number) => {
    return isWithinLimit(currentCount, limits.max_membres);
  }, [limits.max_membres]);

  const canUseAnalyseIA = useCallback(() => {
    return isWithinLimit(usage.analyses_ia_used, limits.max_analyses_ia);
  }, [usage.analyses_ia_used, limits.max_analyses_ia]);

  const canUseOCR = useCallback(() => {
    return isWithinLimit(usage.ocr_used, limits.max_ocr);
  }, [usage.ocr_used, limits.max_ocr]);

  // =========================================================================
  // POURCENTAGES D'USAGE
  // =========================================================================

  const getAnalyseIAUsagePercent = useCallback(() => {
    return calculateUsagePercentage(usage.analyses_ia_used, limits.max_analyses_ia);
  }, [usage.analyses_ia_used, limits.max_analyses_ia]);

  const getOCRUsagePercent = useCallback(() => {
    return calculateUsagePercentage(usage.ocr_used, limits.max_ocr);
  }, [usage.ocr_used, limits.max_ocr]);

  // =========================================================================
  // QUOTAS RESTANTS
  // =========================================================================

  const getRemainingAnalysesIA = useCallback(() => {
    if (limits.max_analyses_ia === -1) return -1; // Illimite
    return Math.max(0, limits.max_analyses_ia - usage.analyses_ia_used);
  }, [usage.analyses_ia_used, limits.max_analyses_ia]);

  const getRemainingOCR = useCallback(() => {
    if (limits.max_ocr === -1) return -1; // Illimite
    return Math.max(0, limits.max_ocr - usage.ocr_used);
  }, [usage.ocr_used, limits.max_ocr]);

  // =========================================================================
  // RAFRAICHIR LES DONNEES DE BILLING USAGE
  // =========================================================================

  const refreshBillingUsage = useCallback(async () => {
    if (!entrepriseId) return;

    try {
      // Appeler la fonction SQL pour obtenir/créer les stats de billing
      const { data, error } = await supabase.rpc('get_or_create_billing_usage', {
        p_entreprise_id: entrepriseId,
      });

      if (error) {
        console.error('[BillingUsage] Error fetching billing usage:', error);
        return;
      }

      if (data && data.length > 0) {
        const row = data[0];
        setBillingUsage({
          chantiersCreatedCount: row.chantiers_created_count || 0,
          freeDeletionUsed: row.free_deletion_used || false,
          cycleStart: row.cycle_start || null,
          cycleEnd: row.cycle_end || null,
        });
      }
    } catch (error) {
      console.error('[BillingUsage] Exception:', error);
    }
  }, [entrepriseId]);

  // =========================================================================
  // RAFRAICHIR L'ABONNEMENT
  // =========================================================================

  const refreshSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Reset to defaults
        setSubscribed(false);
        setPlan("free");
        setStatus("none");
        setBillingPeriod(null);
        setPeriodEnd(null);
        setCancelAtPeriodEnd(false);
        setIsTrialing(false);
        setTrialEnd(null);
        setLimits(DEFAULT_LIMITS);
        setUsage(DEFAULT_USAGE);
        setBillingCycleStart(null);
        setNextResetDate(null);
        setEntrepriseId(null);
        setHasInviteAccess(false);
        setBillingUsage(DEFAULT_BILLING_USAGE);
        setHasStripeSubscription(false);
        setIsFreeTrial(false);
        setFreeTrialDaysLeft(0);
        return;
      }

      // Verifier l'acces invite dans les metadonnees utilisateur
      const inviteAccess = session.user?.user_metadata?.has_invite_access === true;
      setHasInviteAccess(inviteAccess);

      // Verifier le free trial sans CB
      const freeTrialActive = checkFreeTrial(session.user);
      const daysLeft = getFreeTrialDaysLeft(session.user);
      setIsFreeTrial(freeTrialActive);
      setFreeTrialDaysLeft(daysLeft);

      const { data, error } = await supabase.functions.invoke("check-subscription");

      if (error) throw error;

      setSubscribed(data.subscribed || false);
      setPlan(data.plan || "free");
      setStatus(data.status || "none");
      setBillingPeriod(data.billingPeriod || null);
      setPeriodEnd(data.periodEnd || null);
      setCancelAtPeriodEnd(data.cancelAtPeriodEnd || false);
      setIsTrialing(data.isTrialing || false);
      setTrialEnd(data.trialEnd || null);
      setUsage(data.usage || DEFAULT_USAGE);
      setHasStripeSubscription(data.hasStripeSubscription || false);

      // Si free trial actif et pas de subscription Stripe → limites Pro
      if (freeTrialActive && !data.subscribed) {
        setLimits(PLAN_LIMITS.pro);
        setPlan("pro");
      } else {
        setLimits(data.limits || DEFAULT_LIMITS);
      }

      // Cycle de facturation
      const billingStart = data.billingCycleStart || null;
      setBillingCycleStart(billingStart);
      setNextResetDate(calculateNextResetDate(billingStart));
      setEntrepriseId(data.entrepriseId || null);

      // Reset du compteur d'echecs apres un appel reussi
      consecutiveFailures.current = 0;
    } catch (error: any) {
      console.error("Error checking subscription:", error);

      const failures = ++consecutiveFailures.current;

      // Palier 1 : avertissement apres 3 echecs (3 min) — contexte BTP, reseau mobile instable
      if (failures === 3) {
        toast({
          title: "Connexion instable",
          description: "Impossible de vérifier votre abonnement. Vérifiez votre connexion internet.",
          variant: "destructive",
        });
      }

      // Palier 2 : blocage effectif apres 5 echecs (5 min)
      if (failures >= 5) {
        setSubscribed(false);
        setPlan("free");
        setStatus("error");
        setIsTrialing(false);
        setHasStripeSubscription(false);
        setLimits(DEFAULT_LIMITS);
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // OUVRIR LE CHECKOUT (nouveau format)
  // =========================================================================

  const openCheckout = async (targetPlan: Exclude<PlanType, 'free'>, targetBillingPeriod: BillingPeriod) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          plan: targetPlan,
          billingPeriod: targetBillingPeriod,
        }
      });

      if (error) throw error;

      if (data?.url) {
        // Validation sécurité : vérifier que l'URL est bien un domaine Stripe
        if (isValidStripeUrl(data.url)) {
          window.location.href = data.url;
        } else {
          console.error('[Security] URL de checkout invalide:', data.url);
          throw new Error("URL de paiement invalide");
        }
      }
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ouvrir la page de paiement",
        variant: "destructive",
      });
    }
  };

  // Ancien format pour retrocompatibilite
  const openCheckoutLegacy = async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId }
      });

      if (error) throw error;

      if (data?.url) {
        // Validation sécurité : vérifier que l'URL est bien un domaine Stripe
        if (isValidStripeUrl(data.url)) {
          window.location.href = data.url;
        } else {
          console.error('[Security] URL de checkout invalide:', data.url);
          throw new Error("URL de paiement invalide");
        }
      }
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ouvrir la page de paiement",
        variant: "destructive",
      });
    }
  };

  // =========================================================================
  // OUVRIR LE PORTAIL CLIENT
  // =========================================================================

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) {
        console.error('[CustomerPortal] Edge function error:', error);
        throw new Error(error.message || "Erreur lors de l'ouverture du portail");
      }

      // Verifier si l'API a retourne une erreur dans le body
      if (data?.error) {
        console.error('[CustomerPortal] API error:', data.error);
        throw new Error(data.error);
      }

      if (data?.url) {
        // Validation sécurité : vérifier que l'URL est bien un domaine Stripe
        if (isValidStripeUrl(data.url)) {
          window.open(data.url, "_blank");
        } else {
          console.error('[Security] URL de portail invalide:', data.url);
          throw new Error("URL de portail invalide");
        }
      } else {
        throw new Error("Aucune URL retournée par le serveur");
      }
    } catch (error: any) {
      console.error('[CustomerPortal] Exception:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'ouvrir le portail de gestion",
        variant: "destructive",
      });
    }
  };

  // =========================================================================
  // INCREMENTER LES COMPTEURS D'USAGE (avec log persistant)
  // =========================================================================

  const incrementAnalyseIA = async (chantierId?: string) => {
    // Mettre a jour localement immediatement (optimistic update)
    setUsage(prev => ({
      ...prev,
      analyses_ia_used: prev.analyses_ia_used + 1,
    }));

    // Enregistrer dans usage_logs via RPC (persistant, indépendant des chantiers)
    try {
      if (entrepriseId) {
        await supabase.rpc('log_usage', {
          p_entreprise_id: entrepriseId,
          p_type: 'analyse_ia',
          p_chantier_id: chantierId || null,
          p_metadata: {},
        });
      } else {
        // Fallback: utiliser l'ancien RPC si entrepriseId pas encore chargé
        await supabase.rpc('increment_usage_counter', {
          p_entreprise_id: null,
          p_counter_type: 'analyses_ia',
        });
      }
    } catch (error) {
      console.error("Error logging analyse IA usage:", error);
    }
  };

  const incrementOCR = async (chantierId?: string) => {
    // Mettre a jour localement immediatement (optimistic update)
    setUsage(prev => ({
      ...prev,
      ocr_used: prev.ocr_used + 1,
    }));

    // Enregistrer dans usage_logs via RPC (persistant, indépendant des chantiers)
    try {
      if (entrepriseId) {
        await supabase.rpc('log_usage', {
          p_entreprise_id: entrepriseId,
          p_type: 'ocr',
          p_chantier_id: chantierId || null,
          p_metadata: {},
        });
      } else {
        // Fallback: utiliser l'ancien RPC si entrepriseId pas encore chargé
        await supabase.rpc('increment_usage_counter', {
          p_entreprise_id: null,
          p_counter_type: 'ocr',
        });
      }
    } catch (error) {
      console.error("Error logging OCR usage:", error);
    }
  };

  // =========================================================================
  // EFFET INITIAL
  // =========================================================================

  useEffect(() => {
    refreshSubscription();

    // Rafraichir toutes les 60 secondes
    const interval = setInterval(refreshSubscription, 60000);

    // Ecouter les changements d'auth (seulement login/logout, pas les refreshs de token)
    // TOKEN_REFRESHED fire à chaque retour d'onglet (visibilitychange) → inutile de re-appeler l'Edge Function
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        refreshSubscription();
      }
    });

    return () => {
      clearInterval(interval);
      authSub.unsubscribe();
    };
  }, []);

  // =========================================================================
  // EFFET POUR RAFRAICHIR BILLING USAGE QUAND ENTREPRISE ID CHANGE
  // =========================================================================

  useEffect(() => {
    if (entrepriseId) {
      refreshBillingUsage();
    }
  }, [entrepriseId, refreshBillingUsage]);

  return (
    <SubscriptionContext.Provider
      value={{
        // Etat de base
        subscribed,
        plan,
        planName,
        status,
        loading,

        // Acces invite
        hasInviteAccess,

        // Periode et dates
        billingPeriod,
        periodEnd,
        cancelAtPeriodEnd,
        isTrialing,
        trialEnd,

        // Limites et usage
        limits,
        usage,

        // Cycle de facturation
        billingCycleStart,
        nextResetDate,

        // Suivi de facturation (anti-cheat)
        billingUsage,

        // Blocage
        isBlocked,

        // Abonnement Stripe (vs trial DB-only)
        hasStripeSubscription,

        // Free trial sans CB
        isFreeTrial,
        freeTrialDaysLeft,

        // ID entreprise
        entrepriseId,

        // Fonctions de verification
        canCreateChantier,
        canAddUser,
        canAddMembre,
        canUseAnalyseIA,
        canUseOCR,
        checkDeletionAllowed,

        // Rafraîchir billing usage
        refreshBillingUsage,

        // Pourcentages
        getAnalyseIAUsagePercent,
        getOCRUsagePercent,

        // Quotas restants
        getRemainingAnalysesIA,
        getRemainingOCR,

        // Actions
        refreshSubscription,
        openCheckout,
        openCheckoutLegacy,
        openCustomerPortal,
        incrementAnalyseIA,
        incrementOCR,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
